<template>
  <div id='design-portfolio-view'>
    <div class='is-flex is-align-items-center' style="gap: 3rem;">
      <header class='is-flex-grow-1'>
        <DesignPortfolioHeader/>
      </header>
      
      <DesignPortfolioInputs
        @updated='updateTargets'
        @resultsRequested='getResults'
        :loading='loading'
      />
    </div>

    <!-- Summary Statistics -->
    <section class='card' v-if='hasResults'>
      <header class='card-header px-5 py-3'>
        <h2 class='title'>Summary Statistics</h2>
      </header>

      <div class='card-content'>
        <DesignPortfolioSummary :results='results' :metrics='metrics' :targets='targets'/>
      </div>
    </section>

    <!-- Top Alternatives -->
    <section class='card' v-if='hasResults'>
      <header class='card-header'>
        <DesignPortfolioSuggestionsHeader :links='newPortfolioLinks'/>
      </header>

      <div class='card-content'>
        <DesignPortfolioSuggestions :results='results' :targets='targets'/>
      </div>
    </section>

    <!-- No results found -->
    <section class='card' v-if='hasFetchedResults && !hasResults'>
      <header class='card-header px-5 py-3'>
        <h2 class='title'>Unable to find any alternatives</h2>
      </header>
      <div class='card-content'>
        We can help you find replacements for funds containing corporate equities, corporate bonds, sovereign bonds, please try a different starting portfolio.
      </div>
    </section>
    

    <!-- Disclosures -->
    <section v-if='hasResults' class='is-flex is-justify-content-center note'>
      <div class='disclosures'>
        <p>The information contained on this page should not be shared with clients, prospective clients, the general public, or any individual outside of your firm.</p>
        <p><strong>IMPORTANT: The projections or other information generated by YourStake regarding the likelihood of various investment outcomes are hypothetical in nature, do not reflect actual investment results and are not guarantees of future results.</strong></p>
        <p>The information shown is for informational purposes only, and does not constitute an investment recommendation. YourStake applies the following methodology to determine the top alternatives for each style category in your starting portfolio. This tool alone should not determine which securities to buy or sell.</p>
        <p>YourStake does not have any agreement, financial or otherwise, with fund managers to promote their funds as suggestions in this tool. The tool does not favor any investments outside of the methodology described below.</p>
        <p><strong>Methodology</strong></p>
        <p>For each fund in your starting portfolio, the following process is followed to find potential alternatives:</p>
        <p>
          <ol>
            <li>The starting universe of available fund alternatives is any investment fund covered by the 1940 act that has filed its holdings with the SEC (via the Form N-PORT) at least one month prior to the use of the tool. Other investments not considered may have characteristics similar or superior to those being analyzed</li>
            <li>The universe of available fund alternatives is narrowed to those which share a Morningstar Style Category with the fund.</li>
            <li>The resulting universe of funds is then filtered by the “at least as good as” criteria selected, such that any fund that is not at least as good as the fund in the starting portfolio is eliminated from the universe of available funds. We define a fund to be “at least as good as” for the following metrics based on the following criteria: returns – equal or higher. sharpe ratio – equal or higher. alpha – equal or higher. r-squared – equal or higher. shareholder engagement – equal or deeper level of engagement. standard deviation – equal or lower. beta – equal or lower. expense ratio – equal or lower. If a fund in the universe of alternatives does not have a track record in the metric time period selected, it is removed from the universe. If a fund in the starting portfolio does not have a track record in the metric time period selected, that metric is ignored and the universe is not filtered by that metric. For example, if a portfolio is being analyzed for funds with at least as good historical 5y returns, and one of the funds in the starting portfolio does not have a 5y track record, YourStake will not populate any alternatives for that fund.</li>
            <li> The remaining funds are then sorted by overall alignment with the cause areas selected. The top remaining fund is listed as the top alternative in the table.</li>
          </ol>
        </p>
        <p><strong>Overall Alignment</strong></p>
        <p>Overall alignment is a coverage-weighted normalization of the average value of the underlying metric percentile rank within each selected cause area. Alignment numbers displayed in the table are relative to investment style peers. For example, the "Planet Protector" and "Environmental Violations" alignments for an Industrials ETF are based on percentile ranks relative to other Industrials sector funds.</p>
        <p>Coverage weighted average example: Fund A is a 100% equity fund, and fund B has a mix of asset classes, with only a 10% exposure to corporates. If both funds have the same score of 9/10 for toxic air pollution, Fund A will have a better overall alignment because the number applies to the whole portfolio, whereas there are holdings in fund B that the metric does not cover.</p>
        <p>Because the overall alignment is calculated based on the underlying metrics, value sets that contain more metrics will have a higher contribution towards the overall alignment.</p>
        <p><strong>Data Source</strong></p>
        <p>Return and Risk data (Return, Alpha, Beta, Sharpe, Standard Deviation, R-Squared) is from ©2022 Morningstar. All Rights Reserved. The information contained herein: (1) is proprietary to Morningstar and/or its content providers; (2) may not be copied or distributed; and (3) is not warranted to be accurate, complete or timely. Neither Morningstar nor its content providers are responsible for any damages or losses arising from any use of this information. Past performance is no guarantee of future results.</p>
        <p><strong>Calculating Summary Statistics</strong></p>
        <p>Performance numbers displayed for each time period represent a weighted average of trailing performance from Morningstar for all the funds in a portfolio. trailing-performance figures assume reinvestment of both dividends and interest payments as well as capital gains or losses. When returns are unavailable for a holding during a specific time period (i.e., due to it not being in existence during the historical period being reported), the remaining portfolio holdings are re-weighted to maintain consistent proportions. Similarly, holdings that are not funds will be removed from the summary statistics calculation, and the remaining portfolio holdings are re-weighted to maintain consistent proportions. For example, if you have any individual bonds in your starting portfolio, those securities will be ignored in the summary statistics calculations.</p>
        <p>Financial metrics are calculated using the Beginning Assets Weighting method as described by the GIPS reporting standards. This calculation method assumes no rebalancing. The calculation is performed by taking the weighted average of the metric for each underlying fund. For example, portfolio returns are calculated using the following formula where R is total portfolio returns, w<sub>i</sub> is the weight of each holding, and r<sub>i</sub> is the return of each holding R=&#8721;w<sub>i</sub>r<sub>i</sub></p>. To calculate total portfolio returns over 5 years, the following formula is used, where R is total 5y portfolio returns, w<sub>i</sub> is the weight of each fund holding that has data for 5y returns, and r<sub>i</sub> is the return of each fund holding that has data for 5y returns R=&#8721;w<sub>i</sub>r<sub>i</sub>. In this case, each fund's weight in the portfolio is re-weighted to maintain consistent proportions. For example, any individual bond, or a fund with a track record of fewer than 5 years, will be ignored in the overall portfolio returns calculations.</p>
        <p><strong>Interpreting Results</strong></p>
        <p>Results may vary with each use and over time. Historical performance does not guarantee future results.</p>
        <p>All information is historical and not indicative of future results. Current performance may be lower or higher than the quoted past performance, which cannot guarantee results. Share price, principal value, and return will vary, and you may have a gain or a loss when you sell your shares. Performance assumes reinvestment of distributions and does not account for taxes. Performance may not reflect any expense limitation or subsidies currently in effect. Short-term trading fees may apply. To obtain the most recent month-end performance, visit Morningstar.com</p>        
      </div>
    </section>
  </div>
</template>



<script>
  import DesignPortfolioHeader            from '../components/designportfolio/DesignPortfolioHeader.vue'
  import DesignPortfolioInputs            from '../components/designportfolio/DesignPortfolioInputs.vue'
  import DesignPortfolioSummary           from '../components/designportfolio/DesignPortfolioSummary.vue'
  import DesignPortfolioSuggestions       from '../components/designportfolio/DesignPortfolioSuggestions.vue'
  import DesignPortfolioSuggestionsHeader from '../components/designportfolio/DesignPortfolioSuggestionsHeader.vue'


  export default {
    name: 'design-portfolio-view',
    components: {
      DesignPortfolioHeader,
      DesignPortfolioInputs,
      DesignPortfolioSummary,
      DesignPortfolioSuggestions,
      DesignPortfolioSuggestionsHeader
    },
    props: [/* Inputs */],
    data() { return {
      targets: {},
      results: {},
      loading: false,
      hasFetchedResults: false,

      metrics: [
        { name: 'Values Alignment',         key: 'composite', value: 'composite', times: 10, pct: true },
        { name: 'Returns (Net of Fees)',    key: 'returns_',  value: 'financial_data.Load Adjusted Return 5Y Month-End', pct: true },
        { name: 'Sharpe',                   key: 'sharpe_',   value: 'financial_data.Sharpe Ratio 5Y Total Mo-End' },
        { name: 'Beta',                     key: 'beta_',     value: 'financial_data.Beta 5Y Total Mo-End', lowerIsBetter: true },
        { name: 'Alpha',                    key: 'alpha_',    value: 'financial_data.Alpha 5Y Total Mo-End' },
        { name: 'Standard Deviation',       key: 'stdev_',    value: 'financial_data.Standard Deviation 5Y Total Mo-End - Base Currency', lowerIsBetter: true },
        { name: 'R Squared',                key: 'rsquared_', value: 'financial_data.R-Squared 5Y Total Mo-End' },
        { name: 'Weighted Expense Ratio',   key: 'expense',   value: 'ticker__expense_ratio', times: 100, pct: true, lowerIsBetter: true },
      ]
    }},
    beforeCreate() {},
    created() {},
    mounted() {},
    updated() {},
    computed: {
      hasResults() {
        if(!this.results) return false
        return Object.keys(this.results).length
      },
      newPortfolioLinks() {
        if(!this.results) return {}
        return this.results.modelportfolio
      }
    },
    watch: {},
    methods: {
      updateTargets(data) {
        this.targets = data
      },
      async getResults() {
        this.loading = true
        this.hasFetchedResults = false;

        const res = await fetch('/api/v1/design-portfolio-from-portfolio/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken':   this.$root.$data.csrfToken
          },
          body: JSON.stringify({
            starting_universe:    this.targets.universe,
            base_portfolio:       this.targets.portfolio.id,
            screensets:           this.targets.values,
            optimization_choices: this.targets.metrics,
            engagement_level:     this.targets.engagement,
            asset_types:          this.targets.assetTypes,
            esg_mandate:          this.targets.esgMandate,
            share_class:          this.targets.shareClass,
          })
        })

        const data   = await res.json()
        this.results = {...data}
        this.loading = false
        this.hasFetchedResults = true;
        console.log(this.results)
      }
    }
  }
</script>



<style scoped>
  #design-portfolio-view {
    display:        flex;
    flex-direction: column;
    align-items:    stretch;
    gap:            3em;
  }

  #design-portfolio-inputs {
    flex-basis: 55rem;
  }

  .card {
    border: 1px solid gray;
  }

  .disclosures {
    flex-basis: 80em;
  }

  .disclosures p {
    margin: 1em;
  }
</style>



<style>
  #design-portfolio-view .title,
  #design-portfolio-view .label,
  #design-portfolio-view  button,
  #design-portfolio-view .button,
  #design-portfolio-view .b-table th,
  #design-portfolio-view .card-header .subtitle {
    font-family: 'DM Serif Display', serif;
  }

  #design-portfolio-header .subtitle,
  #design-portfolio-view .b-table tr {
    font-family: 'DM Serif', serif;
  }

  #design-portfolio-view .label,
  #design-portfolio-header .subtitle {
    font-size: 1.5rem;
  }

  #design-portfolio-view .card-header .title {
    font-size: 2.25rem;
    margin: 0;
  }

  #design-portfolio-view .note {
    font-size:  0.75rem;
    font-style: italic;
  }

  #design-portfolio-view .input {
    background-color: #fff !important;
  }

  #design-portfolio-view .table-wrapper {
    min-height: 0 !important;
  }
</style>